/**
 * Separador de dia entre grupos de mensagens de uma conversa.
 *
 * As bolhas mostram só a hora (`15:07`). Numa conversa longa isso não diz se a
 * mensagem é de hoje ou da semana passada — a reclamação da equipe em 29/09/2026.
 *
 * `createdAt` é um carimbo de MOMENTO, então a conversão para o fuso local é o
 * comportamento certo aqui: `new Date()` normal, e nada de `date-only.ts` (que
 * existe justamente para o caso oposto, campos que guardam meia-noite).
 */

/**
 * Chave do dia local (`yyyy-MM-dd`) do carimbo. Serve para comparar duas
 * mensagens: chaves diferentes significam dias diferentes e pedem separador.
 */
export function messageDayKey(value: string | Date): string | null {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return null

    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${date.getFullYear()}-${month}-${day}`
}

/**
 * Rótulo do separador: "Hoje", "Ontem", "25 de setembro" no ano corrente e
 * "25 de setembro de 2025" em ano anterior — o ano só aparece quando muda, para
 * não repetir o óbvio em toda conversa.
 *
 * `today` é injetável para o teste não depender do dia em que roda.
 */
export function messageDayLabel(value: string | Date, today = new Date()): string | null {
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return null

    const key = messageDayKey(date)
    if (key === messageDayKey(today)) return 'Hoje'

    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    if (key === messageDayKey(yesterday)) return 'Ontem'

    return new Intl.DateTimeFormat('pt-BR', {
        day: 'numeric',
        month: 'long',
        ...(date.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}),
    }).format(date)
}

/** A mensagem abre um dia novo? Sem anterior, sim: é a primeira da conversa. */
export function startsNewDay(
    current: string | Date,
    previous?: string | Date | null,
): boolean {
    if (!previous) return true
    return messageDayKey(current) !== messageDayKey(previous)
}
